import React from 'react';
import { Box, LinearProgress } from '@mui/material';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import { apiSlice, useGetFilteredDataMutation } from '../redux/slices/apiSlice';
import { setAlertText, setSeverity, setToast } from '../redux/slices/commonComponentSlice';
import { selectProgress, setProgressValue, stopProgress } from '../redux/slices/progressSlice';
import { pricing } from '../redux/slices/pricingSlice';
import { auth } from '../redux/slices/authSlice';
import { mapping, convertToJsonObject } from '../utils/utils';

const GlobalProgressBar = () => {
  const dispatch = useDispatch();
  const progressState = useSelector(selectProgress);
  const { active, value, channelId, selectedAll, invalidateLDIDs, routeId, successMessage } = progressState || {};
  const pricingState = useSelector(pricing);
  const accountId = useSelector(auth)?.userDetails?.account_id;
  const customFiltersRaw = useSelector(pricing)?.config?.custom_filters ?? [];

  const [refetchFiltered] = useGetFilteredDataMutation();

  const pollRef = React.useRef(null);

  React.useEffect(() => {
    if (!active || !channelId) {
      // If not active, ensure no timers are running
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return;
    }

    const token = Cookies.get('access_token');
    const url = `${process.env.REACT_APP_API_URL}/request_channel_details?request_channel_id=${encodeURIComponent(channelId)}`;

    const fetchOnce = async () => {
      try {
        const resp = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true',
          },
        });

        const details = resp?.data?.channel_details ?? resp?.data ?? {};
        let pRaw = details.progress;
        let p = 0;

        if (typeof pRaw === 'string') {
          const m = pRaw.match(/(\d+(?:\.\d+)?)/);
          p = m ? parseFloat(m[1]) : 0;
        } else if (typeof pRaw === 'number') {
          p = pRaw;
        }

        if (Number.isFinite(p)) dispatch(setProgressValue(p));

        const notice = String(details.notice ?? '');
        const errorMsg = String(details.error ?? '').trim();

        const doneByProgress = Number.isFinite(p) && p >= 99.9;
        const doneByNotice = /complete/i.test(notice);
        const hasError = errorMsg.length > 0;

        if (hasError) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
          dispatch(setToast(true));
          dispatch(setAlertText(errorMsg || 'Processing failed'));
          dispatch(setSeverity('error'));
          dispatch(stopProgress());
          return;
        }

        if (doneByProgress || doneByNotice) {
          if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }

          // Refresh or invalidate queries on completion
          const filters = pricingState?.filtered || [];
          if (Array.isArray(filters) && filters.length > 0 && accountId) {
            try {
              const args = {
                id: accountId,
                from: pricingState?.page ? pricingState.pageSize * pricingState.page : 0,
                size: pricingState?.pageSize || 25,
                asc_desc: pricingState?.sorting?.[0]?.asc_desc ?? 'asc',
                sort_by: pricingState?.sorting?.[0]?.sort_by ?? 'sell_offer_cents',
                search: pricingState?.searchParam || '',
                sort: pricingState?.sorting || [],
                state: pricingState?.activeTab === 3 ? 'repriced' : '',
                is_filter_locked: pricingState?.activeTab === 1 ? false : pricingState?.activeTab === 2 ? true : '',
                isSearched: false,
              };
              const body = convertToJsonObject(pricingState?.filtered || [], customFiltersRaw);
              const payload = await refetchFiltered({ args, body }).unwrap();
              dispatch({ type: 'pricing/setUserTable', payload: payload?.data?.map((it) => mapping(it)) || [] });
              dispatch({ type: 'pricing/setPageCount', payload: payload?.total_matching_count || 0 });
            } catch (e) {
              // fallback
              dispatch(apiSlice.util.invalidateTags(['priceTable']));
            }
          } else {
            dispatch(apiSlice.util.invalidateTags(['priceTable']));
          }
          // Always invalidate details on item-details route; otherwise skip for select-all
          const shouldInvalidateDetails = routeId === 'item-details' || !selectedAll;
          if (shouldInvalidateDetails && Array.isArray(invalidateLDIDs)) {
            invalidateLDIDs.filter(Boolean).forEach((id) => {
              dispatch(apiSlice.util.invalidateTags([`Rapnet matching ${id}`]));
            });
          }

          dispatch(setToast(true));
          dispatch(setAlertText(successMessage || 'Processing completed successfully!'));
          dispatch(setSeverity('success'));
          dispatch(stopProgress());
        }
      } catch (err) {
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
        dispatch(setToast(true));
        dispatch(setAlertText(err?.response?.data?.message || 'Polling failed'));
        dispatch(setSeverity('error'));
        dispatch(stopProgress());
      }
    };

    // kick off immediately, then poll
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 5000);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [active, channelId, dispatch, selectedAll, invalidateLDIDs, routeId, successMessage]);

  if (!active) return null;

  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 2000 }}>
      <LinearProgress variant="determinate" value={Math.min(value || 0, 100)} />
    </Box>
  );
};

export default GlobalProgressBar;
